import {
  useCallback,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react';

import { EASE_OUT } from '@/lib/motion-curves';

import { feedMotionSkipped } from './feedMotion';
import './feed.css';

const MORPH_MS = 180;
const SWAP_MS = 90;

const FRAME_HEADER = "[data-testid='feed-frame-header']";
const ROW_TRAIL = "[data-testid='feed-row'] > :nth-child(3)";
// The end of the title line: the row's age and tray, the open card's ⋯ and chevron
const TRAIL = `${FRAME_HEADER} > :nth-child(3), ${ROW_TRAIL}`;

// The card is not faded in: opening, it is drawn whole and the growing height uncovers it; closing, the leaving card
// stays whole above the row while the shrinking height covers it, and goes as the height settles. The title line
// stays where it was drawn either way, so only the end of the line changes, and that alone cross-fades
const fade = (elements: Iterable<Element>, from: number, to: number) => {
  for (const element of elements)
    element.animate([{ opacity: from }, { opacity: to }], {
      duration: SWAP_MS,
      easing: 'ease-out',
      fill: from > to ? 'forwards' : 'backwards',
    });
};

const swap = (leaving: HTMLElement, layer: HTMLElement, content: Element) => {
  layer.replaceChildren(leaving);
  fade(leaving.querySelectorAll(TRAIL), 1, 0);
  fade(content.querySelectorAll(TRAIL), 0, 1);
  // Whole while the height covers it, then gone over the last stretch, where the eased height barely moves and an
  // edge of the leaving card would otherwise linger under the row
  const hold = leaving.animate(
    [{ opacity: 1 }, { opacity: 1, offset: 0.5 }, { opacity: 0 }],
    { duration: MORPH_MS, fill: 'forwards' }
  );
  hold.onfinish = () => leaving.remove();
  hold.oncancel = () => leaving.remove();
};

const morph = (
  element: HTMLElement,
  from: number,
  to: number,
  open: boolean,
  current: MutableRefObject<Animation | null>
) => {
  element.dataset.feedMorphing = open ? 'opening' : 'closing';
  const animation = element.animate(
    [{ height: `${from}px` }, { height: `${to}px` }],
    { duration: MORPH_MS, easing: EASE_OUT }
  );
  current.current = animation;
  const settle = () => {
    if (current.current !== animation) return;
    current.current = null;
    delete element.dataset.feedMorphing;
  };
  animation.onfinish = settle;
  animation.oncancel = settle;
};

const stopMorph = (
  element: HTMLElement,
  current: MutableRefObject<Animation | null>
) => {
  const animation = current.current;
  if (!animation) return;
  current.current = null;
  animation.cancel();
  delete element.dataset.feedMorphing;
};

export const FeedMorph = ({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const leaving = useRef<{ node: HTMLElement; height: number } | null>(null);
  const morphing = useRef<Animation | null>(null);

  const keepLeaving = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    return () => {
      const root = node.parentElement;
      leaving.current = root
        ? { node, height: root.getBoundingClientRect().height }
        : null;
    };
  }, []);

  useLayoutEffect(() => {
    const element = ref.current;
    const content = element?.firstElementChild;
    const layer = layerRef.current;
    const left = leaving.current;
    leaving.current = null;
    if (!element || !content || !layer || !left || left.node.isConnected)
      return;

    stopMorph(element, morphing);
    if (
      typeof element.animate !== 'function' ||
      feedMotionSkipped() ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      layer.replaceChildren();
      return;
    }

    swap(left.node, layer, content);
    const to = content.getBoundingClientRect().height;
    if (Math.abs(to - left.height) >= 1)
      morph(element, left.height, to, open, morphing);
  }, [open]);

  return (
    <div
      ref={ref}
      data-feed-card
      data-active={open || undefined}
      className="relative"
    >
      <div key={open ? 'open' : 'closed'} ref={keepLeaving}>
        {children}
      </div>
      <div
        ref={layerRef}
        aria-hidden="true"
        inert
        data-feed-ghost
        className="pointer-events-none absolute inset-x-0 top-0 z-[2]"
      />
    </div>
  );
};
